import { getUser } from '@app/ducks';
import { getIsViewer } from '@app/logic/User';
import { copyToClipboard } from '@app/utils/clipboard';
import { cn } from '@app/utils/cn';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Input } from '../Fields/Input';
import { Link } from '../Icons';
import { Popover } from '../Popover';
import { Tooltip } from '../Tooltip';

interface Props {
  tooltipText: string;
  shareURL: string;
  message: string;
  popoverOverflowContainer?: boolean;
  className?: string;
  size?: 'medium' | 'large';
  onClick?: () => void;
}

export const ShareButton = ({
  shareURL,
  tooltipText,
  message,
  className,
  onClick,
  popoverOverflowContainer = true,
  size = 'medium',
}: Props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['common']);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const isViewer = getIsViewer(useSelector(getUser));

  const handleCopy = React.useCallback(() => {
    onClick?.();
    copyToClipboard(shareURL);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
  }, [dispatch, shareURL]);

  if (isViewer || isEmpty(shareURL)) {
    return null;
  }

  return (
    <span className={className}>
      <Popover
        isClickable
        placement="bottom-start"
        overflowContainer={popoverOverflowContainer}
        className="sm:max-w-96"
        contentHeader={t('common:link_copied_message')}
        content={
          <>
            <div className="mb-4">{isDefined(message) && message}</div>
            <Input ref={inputRef} size="medium" isReadonly value={shareURL} />
          </>
        }
      >
        <Tooltip content={tooltipText}>
          <button type="button" aria-label={tooltipText} className="inline rounded-full" onClick={handleCopy}>
            <Link className={cn('size-4', { 'size-5': size === 'large' })} />
          </button>
        </Tooltip>
      </Popover>
    </span>
  );
};
